import { useMutation, useQueryClient } from "@tanstack/react-query";
import { workClient } from "../clients/workClient";
import { useWorkScope } from "../components/WorkScopeProvider";
import { unreadWorkRequestCountQueryKey } from "../queries/useUnreadWorkRequestCountQuery";

/**
 * Marks a single Work Request as viewed, clearing its unread item. Fires
 * when the user opens that specific record (list or split view) rather than
 * on page mount — opening one Request must not clear anyone else's. It
 * intentionally skips the success/error toast other Work Request mutations
 * show: a toast on every row click would be noise, not feedback.
 */
export function useMarkWorkRequestViewedMutation() {
  const queryClient = useQueryClient();
  const { selectedCustomer, selectedFacility } = useWorkScope();

  return useMutation({
    mutationKey: ["work", "requests", "mark-viewed"],
    mutationFn: async (workRequestId: string) => {
      if (!selectedCustomer || !selectedFacility) {
        throw new Error("A customer and facility must be selected.");
      }

      const response = await workClient.v1.customers[":customerId"].facilities[
        ":facilityId"
      ].requests[":requestId"]["mark-viewed"].$post({
        param: {
          customerId: selectedCustomer.id,
          facilityId: selectedFacility.id,
          requestId: workRequestId,
        },
      });
      if (!response.ok) {
        throw new Error(
          `Failed to mark the Work Request as viewed: ${response.status}`,
        );
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [unreadWorkRequestCountQueryKey],
      });
      // Clears the stale `isUnread` flag cached on the queue row and on the
      // open detail pane, for both an explicit click and a deep-linked open.
      void queryClient.invalidateQueries({ queryKey: ["work-requests"] });
      void queryClient.invalidateQueries({ queryKey: ["work-request"] });
    },
  });
}
